import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import { Button, Card, Field, Input, Textarea, Select, Chip, Note, Modal, Empty, Icon, money } from '../ui';
import { fmtDateTime } from '../utils/date';

/** SMS (CallPro Text) — СӨХ-өөс оршин суугчдад мессеж: бүх айл / байр / өртэй айл / сонгосон айл / гар дугаар.
 *  Загварын хувьсагч: {сөх} {айл} {үлдэгдэл} {линк} {сар} {он}. Түүх batch-аар, хүргэлтийн төлөв CallPro-оос. */
type Cfg = { enabled: boolean; sender: string; balances: { operator: string; balance?: number; current?: number; total?: number; error?: string }[]; templates: Record<string, string>; hint: string };
type Preview = { recipients: number; segments: number; sample: string; units_without_phone: number; list: { unit: string; phone: string; balance: number }[] };
type Batch = { batch_id: string; created_at: string | null; total: number; delivered: number; failed: number; undelivered: number; kind: string; sample: string; segments: number };
type Row = { id: string; unit: string; phone: string; text: string; status: string; error: string; segments: number; delivered_at: string | null };

const OP: Record<string, string> = { mobicom: 'Мобиком', unitel: 'Юнител', skytel: 'Скайтел' };
const KIND: Record<string, string> = { custom: 'Мессеж', reminder: 'Төлбөрийн сануулга', announcement: 'Зарлал' };
const ST: Record<string, { label: string; tone: 'paid' | 'debt' | 'progress' | 'new' }> = {
  queued: { label: 'Илгээсэн', tone: 'progress' }, delivered: { label: 'Хүрсэн', tone: 'paid' },
  undelivered: { label: 'Хүрээгүй', tone: 'debt' }, failed: { label: 'Алдаа', tone: 'debt' },
};
const segs = (t: string) => { const per = /^[\x00-\x7F]*$/.test(t) ? 160 : 70; return Math.max(1, Math.ceil(t.length / per)); };

export default function SmsPage() {
  const [cfg, setCfg] = useState<Cfg | null>(null);
  const [buildings, setBuildings] = useState<string[]>([]);
  const [mode, setMode] = useState<'all' | 'building' | 'debt' | 'phones'>('all');
  const [building, setBuilding] = useState('');
  const [phones, setPhones] = useState('');
  const [kind, setKind] = useState<'custom' | 'reminder' | 'announcement'>('custom');
  const [text, setText] = useState('');
  const [prev, setPrev] = useState<Preview | null>(null);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(''); const [err, setErr] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [history, setHistory] = useState<Batch[]>([]);
  const [open, setOpen] = useState<{ b: Batch; rows: Row[] } | null>(null);

  const note = (m: string) => { setMsg(m); setTimeout(() => setMsg(''), 4000); };
  const fail = (e: any, d: string) => { let t = d; try { t = JSON.parse(e?.body || '{}')?.detail || d; } catch { t = e?.message || d; } setErr(t); setTimeout(() => setErr(''), 7000); };

  const loadHistory = useCallback(() => api.get<any>('/admin/sms').then(r => setHistory(r.items || [])).catch(() => {}), []);
  useEffect(() => {
    api.get<Cfg>('/admin/sms/config').then(setCfg).catch(() => setCfg({ enabled: false, sender: '', balances: [], templates: {}, hint: 'Тохиргоо уншиж чадсангүй' }));
    api.get<any>('/admin/buildings').then(r => setBuildings((r.items || []).map((b: any) => b.name || b.building || b))).catch(() => {});
    loadHistory();
  }, [loadHistory]);

  const target = useMemo(() => ({ mode, building: mode === 'building' ? building : undefined, phones: mode === 'phones' ? phones.split(/[\s,;]+/).filter(Boolean) : undefined }), [mode, building, phones]);
  useEffect(() => {
    const t = setTimeout(() => { api.post<Preview>('/admin/sms/preview', { ...target, text, kind }).then(setPrev).catch(() => setPrev(null)); }, 350);
    return () => clearTimeout(t);
  }, [target, text, kind]);

  const pickKind = (k: typeof kind) => { setKind(k); if (k !== 'custom' && cfg?.templates[k] && !text) setText(cfg.templates[k]); if (k === 'reminder') setMode('debt'); };
  const send = async () => {
    setConfirm(false); setBusy('send');
    try {
      const r: any = await api.post('/admin/sms/send', { ...target, text, kind });
      note(`${r.sent} дугаарт илгээлээ${r.failed ? ` · ${r.failed} алдаатай${r.error ? ` (${r.error})` : ''}` : ''}`);
      setText(''); loadHistory();
    } catch (e) { fail(e, 'Илгээж чадсангүй'); } finally { setBusy(''); }
  };
  const refresh = async () => { setBusy('refresh'); try { const r: any = await api.post('/admin/sms/refresh', {}); note(`${r.checked} мессеж шалгаж, ${r.updated} шинэчлэв`); loadHistory(); } catch (e) { fail(e, 'Шалгаж чадсангүй'); } finally { setBusy(''); } };
  const openBatch = async (b: Batch) => { try { const r: any = await api.get(`/admin/sms/${b.batch_id}`); setOpen({ b, rows: r.items || [] }); } catch (e) { fail(e, 'Уншиж чадсангүй'); } };

  const nseg = segs(prev?.sample || text || '');
  const cost = (prev?.recipients || 0) * nseg;

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--ink)' }}>SMS илгээх</h2>
        {cfg?.enabled && <span style={{ fontSize: 13, color: 'var(--muted)' }}>Илгээгч дугаар: <b>{cfg.sender}</b></span>}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {cfg?.balances?.map(b => (
            <span key={b.operator} className="sh-chip" title={b.error || `Өнөөдөр илгээсэн ${b.current ?? 0} / ${b.total ?? 0}`} style={{ fontSize: 12 }}>
              {OP[b.operator] || b.operator}: {b.error ? '—' : <b>{b.balance}</b>}
            </span>
          ))}
        </div>
      </div>
      {msg && <Note icon="check">{msg}</Note>}
      {err && <Note tone="danger" icon="alert">{err}</Note>}
      {cfg && !cfg.enabled && <Note tone="warn" icon="info">{cfg.hint || 'SMS тохируулаагүй'}</Note>}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 14 }} className="sms-grid">
        <Card style={{ display: 'grid', gap: 12 }}>
          <div style={{ fontWeight: 700 }}>1. Хэнд илгээх</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <Chip tone="filter" on={mode === 'all'} onClick={() => setMode('all')}>Бүх айл</Chip>
            <Chip tone="filter" on={mode === 'debt'} onClick={() => setMode('debt')}>Зөвхөн өртэй айл</Chip>
            <Chip tone="filter" on={mode === 'building'} onClick={() => setMode('building')}>Байраар</Chip>
            <Chip tone="filter" on={mode === 'phones'} onClick={() => setMode('phones')}>Дугаар оруулах</Chip>
          </div>
          {mode === 'building' && <Field label="Байр"><Select value={building} onChange={e => setBuilding(e.target.value)}><option value="">— сонгох —</option>{buildings.map(b => <option key={b} value={b}>{b}</option>)}</Select></Field>}
          {mode === 'phones' && <Field label="Утасны дугаарууд (таслал, зай, мөрөөр)"><Textarea value={phones} onChange={e => setPhones(e.target.value)} rows={3} placeholder="99112233, 88001122" /></Field>}

          <div style={{ fontWeight: 700, marginTop: 4 }}>2. Мессеж</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {(['custom', 'reminder', 'announcement'] as const).map(k => <Chip key={k} tone="filter" on={kind === k} onClick={() => pickKind(k)}>{KIND[k]}</Chip>)}
          </div>
          <Field label="Агуулга" help="Хувьсагч: {сөх} {айл} {үлдэгдэл} {линк} {сар} {он} — айл бүрт өөрийн утгаар солигдоно">
            <Textarea value={text} onChange={e => setText(e.target.value)} rows={5} maxLength={500} placeholder="Жишээ: {сөх}: {сар}-р сарын төлбөр {үлдэгдэл}₮ төлөгдөөгүй. Төлөх: {линк}" />
          </Field>
          <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>{text.length} тэмдэгт · {nseg} сегмент{/^[\x00-\x7F]*$/.test(text) ? ' (латин 160/сегмент)' : ' (кирилл 70/сегмент)'}</div>
          <Button icon="message" loading={busy === 'send'} disabled={!cfg?.enabled || !text.trim() || !prev?.recipients} onClick={() => setConfirm(true)}>
            Илгээх{prev?.recipients ? ` · ${prev.recipients} дугаар` : ''}
          </Button>
        </Card>

        <Card style={{ display: 'grid', gap: 10, alignContent: 'start' }}>
          <div style={{ fontWeight: 700 }}>Урьдчилан харах</div>
          {prev ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                <Stat label="Хүлээн авагч" value={String(prev.recipients)} />
                <Stat label="Сегмент / мессеж" value={String(nseg)} />
                <Stat label="Нийт SMS" value={String(cost)} />
              </div>
              {prev.units_without_phone > 0 && mode !== 'phones' && <Note tone="warn" icon="info">{prev.units_without_phone} айлд утасны дугаар бүртгэгдээгүй — тэдэнд SMS очихгүй.</Note>}
              {prev.sample && <div style={{ background: 'var(--bg)', borderRadius: 'var(--r-md)', padding: '10px 12px', fontSize: 14, whiteSpace: 'pre-wrap', lineHeight: 1.45 }}><div style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 4 }}>Эхний хүлээн авагчид очих хэлбэр:</div>{prev.sample}</div>}
              {prev.list.length > 0 && (
                <div style={{ maxHeight: 260, overflow: 'auto', fontSize: 13 }}>
                  {prev.list.map((r, i) => <div key={i} style={{ display: 'flex', gap: 8, padding: '4px 0', borderBottom: '1px dashed var(--line)' }}><span style={{ flex: 1 }}>{r.unit || '—'}</span><span className="tnum">{r.phone}</span>{r.balance > 0 && <span className="tnum" style={{ color: 'var(--red)', minWidth: 90, textAlign: 'right' }}>{money(r.balance)}</span>}</div>)}
                  {prev.recipients > prev.list.length && <div style={{ color: 'var(--muted)', padding: '6px 0' }}>… нийт {prev.recipients}</div>}
                </div>
              )}
            </>
          ) : <div style={{ color: 'var(--muted)', fontSize: 13 }}>Хүлээн авагч, мессежээ сонгоход энд харагдана.</div>}
        </Card>
      </div>

      <Card style={{ padding: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '1px solid var(--line)' }}>
          <b>Илгээсэн түүх</b>
          <Button size="sm" variant="secondary" icon="refresh" loading={busy === 'refresh'} disabled={!cfg?.enabled} onClick={refresh} style={{ marginLeft: 'auto' }}>Хүргэлт шалгах</Button>
        </div>
        {history.length === 0 ? <Empty icon="message" title="Илгээсэн SMS алга" /> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead><tr style={{ textAlign: 'left', color: 'var(--muted)', fontSize: 13 }}><th style={th}>Огноо</th><th style={th}>Төрөл</th><th style={th}>Мессеж</th><th style={th}>Дугаар</th><th style={th}>Хүрсэн</th><th style={th}>Алдаа</th><th style={th}></th></tr></thead>
            <tbody>{history.map(b => (
              <tr key={b.batch_id} style={{ borderTop: '1px solid var(--line)', cursor: 'pointer' }} onClick={() => openBatch(b)}>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>{b.created_at ? fmtDateTime(b.created_at) : ''}</td>
                <td style={td}>{KIND[b.kind] || b.kind}</td>
                <td style={{ ...td, maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={b.sample}>{b.sample}</td>
                <td style={td}>{b.total} <span style={{ color: 'var(--muted)', fontSize: 12 }}>({b.segments} SMS)</span></td>
                <td style={td}><span style={{ color: b.delivered ? 'var(--green)' : 'var(--muted)' }}>{b.delivered}</span>{b.undelivered ? <span style={{ color: 'var(--red)' }}> · {b.undelivered} хүрээгүй</span> : null}</td>
                <td style={{ ...td, color: b.failed ? 'var(--red)' : 'var(--muted)' }}>{b.failed || '—'}</td>
                <td style={td}><Icon name="chevron-right" size={16} style={{ color: 'var(--muted)' }} /></td>
              </tr>))}
            </tbody>
          </table>
        )}
      </Card>

      {confirm && (
        <Modal title="SMS илгээх үү?" onClose={() => setConfirm(false)} footer={<><Button variant="secondary" onClick={() => setConfirm(false)}>Болих</Button><Button onClick={send}>Илгээх</Button></>}>
          <div style={{ fontSize: 14, lineHeight: 1.6 }}>
            <b>{prev?.recipients}</b> дугаарт, мессеж бүр <b>{nseg}</b> сегмент — нийт <b>{cost}</b> SMS зарцуулна.
            {prev?.sample && <div style={{ background: 'var(--bg)', borderRadius: 8, padding: 10, marginTop: 10, whiteSpace: 'pre-wrap' }}>{prev.sample}</div>}
          </div>
        </Modal>
      )}
      {open && (
        <Modal title={`${KIND[open.b.kind] || 'SMS'} · ${open.b.created_at ? fmtDateTime(open.b.created_at) : ''}`} width={680} onClose={() => setOpen(null)}>
          <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
              <thead><tr style={{ textAlign: 'left', color: 'var(--muted)', fontSize: 12.5 }}><th style={th}>Айл</th><th style={th}>Дугаар</th><th style={th}>Төлөв</th><th style={th}>Мессеж</th></tr></thead>
              <tbody>{open.rows.map(r => (
                <tr key={r.id} style={{ borderTop: '1px solid var(--line)' }}>
                  <td style={td}>{r.unit || '—'}</td><td style={{ ...td, whiteSpace: 'nowrap' }} className="tnum">{r.phone}</td>
                  <td style={td}><Chip tone={ST[r.status]?.tone || 'new'}>{ST[r.status]?.label || r.status}</Chip>{r.error && <div style={{ fontSize: 11.5, color: 'var(--red)' }}>{r.error}</div>}</td>
                  <td style={{ ...td, fontSize: 12.5, color: 'var(--muted)' }}>{r.text}</td>
                </tr>))}
              </tbody>
            </table>
          </div>
        </Modal>
      )}
      <style>{`@media (max-width: 760px) { .sms-grid { grid-template-columns: 1fr !important; } }`}</style>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return <div style={{ background: 'var(--bg)', borderRadius: 'var(--r-md)', padding: '8px 10px' }}><div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{label}</div><div className="tnum" style={{ fontSize: 20, fontWeight: 800 }}>{value}</div></div>;
}
const th: React.CSSProperties = { padding: '8px 12px', fontWeight: 600 };
const td: React.CSSProperties = { padding: '9px 12px', verticalAlign: 'top' };
